import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity, DocumentType } from "@erp/shared-types";
import { extendSchemaEnum } from "./extract-document-content.step";

/**
 * Extract Document Content Step v2
 *
 * Extends v1 with support for admin-configurable extraction schemas
 * (extractionSchemaDefinitionId). See v1 for base behavior documentation.
 *
 * Reuses the 8-value `extendSchemaEnum` from V1 so the unified config
 * picker in `ExtractDocumentContentConfigRenderer` can offer the same
 * built-in options for both versions of the step. Picking a built-in
 * here goes through the hard-coded extraction config; picking
 * `extractionSchemaDefinitionId` (V2-only) goes through the stored
 * `SchemaDefinition` resolution path.
 */

/**
 * Document type filter enum - matches DocumentType from shared-types
 */
const documentTypeEnum = z.enum(DocumentType);

export const extractDocumentContentV2Step = defineStepConfig({
  type: "extract_document_content_v2",
  name: "Extract Document Content (V2)",
  description:
    "Run OCR and content extraction on document attachments with configurable extraction schemas",
  category: "AI & Agents",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email whose attachments should be processed",
    ),
    extractionConfig: z
      .object({
        type: z.literal("EXTRACT"),
        schema: z.record(z.string(), z.unknown()),
        advancedOptions: z.record(z.string(), z.unknown()).optional(),
      })
      .describe(
        "Optional extraction schema; defaults to Extend AI schema for the configured type",
      )
      .optional(),
    // Email origin metadata — passed through for downstream reply detection
    originType: z
      .string()
      .optional()
      .describe(
        'Email origin classification: "external", "self_sent", or "reply_to_outbound"',
      ),
    conversationId: z
      .string()
      .optional()
      .describe(
        "Microsoft Graph conversation/thread ID for tracing replies to orders",
      ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email for downstream steps",
    ),
    originType: z
      .string()
      .optional()
      .describe("Pass-through of email origin classification"),
    conversationId: z
      .string()
      .optional()
      .describe("Pass-through of conversation/thread ID"),
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe(
        "Array of document entity references (for fanout to per-document processing)",
      ),
    documentOcrResults: z.array(
      z.object({
        document: entityOfType(SystemEntity.CORE_DOCUMENT),
        ocrResult: entityOfType(
          SystemEntity.CORE_DOCUMENT_OCR_RESULT,
        ).optional(),
        status: z.enum(["completed", "failed", "skipped"]),
        extractedTextLength: z.number().optional(),
        error: z.string().optional(),
        // Populated only when the handler ran in eval mode (context.isEvalRun
        // === true) and persistence was skipped. Carries the in-memory OCR
        // payload so downstream evaluators can compare without hitting the DB
        // for a row that was never written.
        extractedResultInline: z.record(z.string(), z.unknown()).optional(),
      }),
    ),
    processedCount: z.number(),
    failedCount: z.number(),
    skippedCount: z.number(),
  }),

  configSchema: z.object({
    extendSchemaType: extendSchemaEnum.optional(),
    extractionSchemaDefinitionId: z
      .uuid()
      .optional()
      .describe(
        "Optional stored extraction schema definition to compile and use for all processed documents",
      ),
    skipClassificationCheck: z
      .boolean()
      .optional()
      .describe(
        "Skip document classification checks entirely (processes all documents regardless of classification)",
      ),
    filterForDocumentTypes: z
      .array(documentTypeEnum)
      .optional()
      .describe(
        "Only process documents with these classification types. If specified, documents not matching these types are skipped. Overrides the default OTHER-exclusion behavior.",
      ),
    useClassificationBasedSchema: z
      .boolean()
      .optional()
      .describe(
        "Dynamically select extraction schema based on each document's classification type. When enabled, ignores the extendSchemaType config and determines the appropriate schema per document unless a stored extraction schema definition is configured.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
    nonRetryableErrorTypes: [
      "InvalidFileFormatError",
      "UnsupportedMimeTypeError",
    ],
  },

  // Surfaces the Evals tab in the step editor. The evaluator
  // (ExtractDocumentContentV2Evaluator) reshapes step output during seeding
  // into { extractions: { [documentId]: extractedResult } } and field-diffs
  // against that shape at run time.
  evalConfig: {
    evalOutputKeys: ["extractions"],
    outputFormat: "object",
  },
});
